import { useGetSites } from '@shared/api';
import { useGetOrganizationTimePeriods } from '@shared/api/hooks/ccf/periods';
import { getReportsPathMap } from '@app/components/reports/utils';
import { purchasedGoodsRoutes } from '@app/purchased-goods/purchased-goods-routes';
import { soldGoodsRoutes } from '@app/sold-goods/sold-goods-routes';
import {
  ActivityImportList,
  ActivityImportStatusEnum,
  OperationReportTypeEnum,
  Site,
  SiteSourceEnum,
  Tag,
  TimePeriod,
  TimePeriodSourceEnum,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { TagCell } from '@shared/components/data-table/cells/tag-cell';
import { Progress } from '@shared/components/ui/progress';
import { Skeleton } from '@shared/components/ui/skeleton';
import { formatDate, getObjectFromArrayWithKey } from '@shared/lib/utils';
import { ColumnDef } from '@tanstack/react-table';
import { generatePath, useNavigate } from 'react-router-dom';

function importFilterQuery(uuid: string) {
  return `?activitiesImport=${uuid}`;
}

function getFoodOrPackagingPath(
  reportType: OperationReportTypeEnum,
  activityType: 'food' | 'packaging',
  uuid: string
): string {
  const pathMap = getReportsPathMap()[reportType as 'purchase' | 'sales'];
  const base =
    activityType === 'packaging' ? pathMap.packagingData : pathMap.foodData;
  return `${generatePath(base!)}${importFilterQuery(uuid)}`;
}

function getExcludedItemsPath(
  reportType: OperationReportTypeEnum,
  category: 'review' | 'other',
  uuid: string
): string {
  const isSales = reportType === OperationReportTypeEnum.sales;
  const base = isSales
    ? category === 'other'
      ? soldGoodsRoutes.SOLD_EXCLUDED_ITEMS_OTHER
      : soldGoodsRoutes.SOLD_EXCLUDED_ITEMS_UNCERTAIN
    : category === 'other'
      ? purchasedGoodsRoutes.PURCHASED_EXCLUDED_ITEMS_OTHER
      : purchasedGoodsRoutes.PURCHASED_EXCLUDED_ITEMS_REVIEW;
  return `${generatePath(base)}${importFilterQuery(uuid)}`;
}

/** Real per-import counts by current review state, from the list endpoint's
 * `categoryBreakdown` (typed locally until the imports tag is regenerated via
 * Orval — see shared/api rules). Buckets are disjoint: `review` = uncertain +
 * flagged goods; `food`/`packaging` count clean goods only. */
interface ImportCategoryBreakdown {
  food: number;
  packaging: number;
  other: number;
  review: number;
}

function getImportBreakdown(row: ActivityImportList) {
  const failed = Number(row.numberOfFailedRows) || 0;
  const breakdown = (
    row as ActivityImportList & { categoryBreakdown?: ImportCategoryBreakdown }
  ).categoryBreakdown;
  return {
    food: breakdown?.food ?? 0,
    packaging: breakdown?.packaging ?? 0,
    other: breakdown?.other ?? 0,
    review: breakdown?.review ?? 0,
    failed,
  };
}

export function useActivityImportTableColumns({
  reportType,
}: {
  reportType: OperationReportTypeEnum;
}): ColumnDef<ActivityImportList>[] {
  const navigate = useNavigate();
  const { data: periods, isLoading: isPeriodsLoading } =
    useGetOrganizationTimePeriods();

  const { data: sites, isLoading: isSitesLoading } = useGetSites({
    page: 1,
    page_size: 1000,
  });

  const { results: sitesOptions } = sites || {};
  return [
    {
      accessorKey: 'uuid',
      enableHiding: true,
    },

    {
      accessorKey: 'created',
      header: ({ column }) => <HeaderCell column={column} name="Upload date" />,
      cell: ({ getValue }) => {
        const date = getValue<string>();
        return <Text>{formatDate(date, 'do MMM yyyy')}</Text>;
      },
    },
    {
      id: 'fileName',
      accessorKey: 'fileName',
      header: ({ column }) => <HeaderCell column={column} name="Name" />,
      cell: ({ getValue }) => {
        const fileName = getValue<string>();
        return <Text className="min-w-48">{fileName}</Text>;
      },
      enableColumnFilter: true,
      meta: {
        label: 'Name',
        variant: 'text',
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      accessorKey: 'site',
      header: ({ column }) => <HeaderCell column={column} name="Site" />,
      cell: ({ getValue, row }) => {
        const siteUuid = getValue<Site['uuid']>();
        const siteSource = row.original.siteSource;

        if (siteSource === SiteSourceEnum['not_assigned']) {
          return <Text variant="subtle">Not assigned</Text>;
        }

        if (siteSource === SiteSourceEnum['from_column']) {
          return <Text variant="subtle">From column</Text>;
        }

        const siteName =
          siteUuid &&
          sitesOptions &&
          getObjectFromArrayWithKey('uuid', siteUuid, sitesOptions)?.name;

        if (isSitesLoading) {
          return <Skeleton className="h-6 w-full" />;
        }

        if (siteSource === SiteSourceEnum['from_import'] && siteName) {
          return <Text>{siteName}</Text>;
        }
      },
    },
    {
      accessorKey: 'timePeriod',
      header: ({ column }) => <HeaderCell column={column} name="Period" />,
      cell: ({ getValue, row }) => {
        const timePeriodUuid = getValue<TimePeriod['uuid']>();
        const timePeriodSource = row.original.timePeriodSource;

        if (isPeriodsLoading) {
          return <Skeleton className="h-6 w-full" />;
        }

        if (timePeriodSource === TimePeriodSourceEnum['from_column']) {
          return <Text variant="subtle">From column</Text>;
        } else {
          const timePeriodName =
            timePeriodUuid && periods
              ? getObjectFromArrayWithKey('uuid', timePeriodUuid, periods)?.name
              : null;
          return <span>{timePeriodName}</span>;
        }
      },
    },
    {
      accessorKey: 'tags',
      header: ({ column }) => <HeaderCell column={column} name="Tags" />,
      cell: ({ getValue }) => {
        const tags = getValue<Tag[]>();
        return <TagCell tags={tags} />;
      },
    },
    {
      accessorKey: 'numberOfRows',
      header: ({ column }) => <HeaderCell column={column} name="Rows" />,
    },
    {
      accessorKey: 'status',
      // No header text — the column shows either progress or a result breakdown.
      header: () => null,
      minSize: 300,
      cell: ({ getValue, row }) => {
        const status = getValue<ActivityImportStatusEnum>();
        const {
          original: { numberOfRows, numberOfImportedRows, uuid },
        } = row;
        const completionPercentage = Math.floor(
          (Number(numberOfImportedRows) * 100) / Number(numberOfRows)
        );

        switch (status) {
          case ActivityImportStatusEnum.importing:
          case ActivityImportStatusEnum.processing:
            if (
              status === ActivityImportStatusEnum.importing ||
              numberOfImportedRows === 0
            ) {
              return (
                <div className="flex min-w-[220px] flex-row items-center space-x-2">
                  <Icons.clock className="size-5 text-gray-500" />
                  <span className="text-sm">Queued for processing</span>
                </div>
              );
            }
            return (
              <div className="flex min-w-[220px] flex-row items-center space-x-2">
                <Progress
                  value={completionPercentage}
                  indicatorBgColor="bg-teal-400"
                />
                <span className="w-8 text-right text-sm text-gray-600">
                  {completionPercentage}%
                </span>
              </div>
            );
          case ActivityImportStatusEnum.completed: {
            const breakdown = getImportBreakdown(row.original);
            const items = [
              {
                key: 'food',
                icon: <Icons.carrot className="size-4 text-gray-600" />,
                count: breakdown.food,
                to: getFoodOrPackagingPath(reportType, 'food', uuid),
              },
              {
                key: 'packaging',
                icon: <Icons.package className="size-4 text-gray-600" />,
                count: breakdown.packaging,
                to: getFoodOrPackagingPath(reportType, 'packaging', uuid),
              },
              {
                key: 'other',
                icon: <Icons.cookingPot className="size-4 text-gray-600" />,
                count: breakdown.other,
                to: getExcludedItemsPath(reportType, 'other', uuid),
              },
              {
                key: 'review',
                icon: <Icons.help className="size-4 text-gray-600" />,
                count: breakdown.review,
                to: getExcludedItemsPath(reportType, 'review', uuid),
              },
            ].filter((item) => item.count > 0);

            return (
              <div className="flex min-w-[280px] flex-wrap items-center gap-x-4 gap-y-1">
                {items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className="flex items-center gap-1 text-sm hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(item.to);
                    }}
                  >
                    {item.icon}
                    <span className="font-medium text-teal-600">
                      {item.count}
                    </span>
                  </button>
                ))}
                {breakdown.failed > 0 && (
                  <span className="flex items-center gap-1 text-sm text-error-600">
                    <Icons.x className="size-4" />
                    {breakdown.failed} failed
                  </span>
                )}
              </div>
            );
          }
          case ActivityImportStatusEnum.processing_failed:
            return (
              <div className="flex min-w-[220px] flex-row items-center space-x-2">
                <Icons.x className="size-5 text-error-500" />
                <span className=" text-sm text-error-600">Import failed</span>
              </div>
            );
        }
      },
    },
  ];
}
